// Úkoly: seznam podle filtru, optimistické Splněno s vrácením při chybě, vytvoření, úprava a smazání.
// Smlouva nemá GET /tasks/:id, úkol pro úpravu se najde v seznamu.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { CreateTaskInput, TaskItem, UpdateTaskInput } from '@wac-buddy/shared'
import { createTask, deleteTask, listTasks, updateTask, type TaskFilter } from '@/api/tasks'
import { useJobsStore } from './jobs'

export type ListStatus = 'idle' | 'loading' | 'ready' | 'error'
export type TaskListFilter = Extract<TaskFilter, 'open' | 'overdue' | 'today' | 'done'>

export const TASK_LIST_FILTERS: TaskListFilter[] = ['open', 'overdue', 'today', 'done']

const PAGE_SIZE = 50
const FIND_PAGE_SIZE = 200
const FIND_MAX_PAGES = 5

function belongs(filter: TaskListFilter, status: TaskItem['status']): boolean {
  return filter === 'done' ? status === 'done' : status === 'open'
}

export const useTasksStore = defineStore('tasks', () => {
  const filter = ref<TaskListFilter>('open')
  const items = ref<TaskItem[]>([])
  const nextCursor = ref<string | null>(null)
  const status = ref<ListStatus>('idle')
  const loadingMore = ref(false)

  let requestSeq = 0

  async function load(): Promise<void> {
    const seq = ++requestSeq
    status.value = 'loading'
    try {
      const res = await listTasks({ filter: filter.value, limit: PAGE_SIZE })
      if (seq !== requestSeq) return
      items.value = res.data
      nextCursor.value = res.nextCursor
      status.value = 'ready'
    } catch {
      if (seq !== requestSeq) return
      status.value = 'error'
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || loadingMore.value) return
    const seq = requestSeq
    loadingMore.value = true
    try {
      const res = await listTasks({
        filter: filter.value,
        cursor: nextCursor.value,
        limit: PAGE_SIZE,
      })
      if (seq !== requestSeq) return
      items.value = [...items.value, ...res.data]
      nextCursor.value = res.nextCursor
    } catch {
      /* tlačítko zůstane, uživatel to zkusí znovu */
    } finally {
      loadingMore.value = false
    }
  }

  async function setFilter(next: TaskListFilter): Promise<void> {
    if (filter.value === next && status.value === 'ready') return
    filter.value = next
    items.value = []
    nextCursor.value = null
    await load()
  }

  function forgetRelated(t: { jobId?: string | null }): void {
    if (t.jobId) delete useJobsStore().details[t.jobId]
  }

  function applyLocalStatus(id: string, next: TaskItem['status']): () => void {
    const index = items.value.findIndex((t) => t.id === id)
    if (index < 0) return () => undefined
    const before = items.value[index]!
    const updated: TaskItem = {
      ...before,
      status: next,
      doneAt: next === 'done' ? new Date().toISOString() : null,
      overdue: next === 'open' ? before.overdue : false,
    }
    if (belongs(filter.value, next)) items.value.splice(index, 1, updated)
    else items.value.splice(index, 1)
    return () => {
      const at = items.value.findIndex((t) => t.id === id)
      if (at >= 0) {
        items.value.splice(at, 1, before)
        return
      }
      items.value.splice(Math.min(index, items.value.length), 0, before)
    }
  }

  async function changeStatus(id: string, next: 'open' | 'done'): Promise<TaskItem> {
    const rollback = applyLocalStatus(id, next)
    try {
      const saved = await updateTask(id, { status: next })
      forgetRelated(saved)
      return saved
    } catch (e) {
      rollback()
      throw e
    }
  }

  function markDone(id: string): Promise<TaskItem> {
    return changeStatus(id, 'done')
  }

  function reopen(id: string): Promise<TaskItem> {
    return changeStatus(id, 'open')
  }

  /** Úkol pro formulář úpravy: z načteného seznamu, jinak procházením všech úkolů. Nenalezený vrátí null. */
  async function findOne(id: string): Promise<TaskItem | null> {
    const cached = items.value.find((t) => t.id === id)
    if (cached) return cached
    let cursor: string | null = null
    for (let page = 0; page < FIND_MAX_PAGES; page++) {
      const res = await listTasks({ filter: 'all', cursor, limit: FIND_PAGE_SIZE })
      const hit = res.data.find((t) => t.id === id)
      if (hit) return hit
      cursor = res.nextCursor
      if (!cursor) break
    }
    return null
  }

  async function create(input: CreateTaskInput): Promise<TaskItem> {
    const saved = await createTask(input)
    forgetRelated(saved)
    status.value = 'idle'
    return saved
  }

  async function update(id: string, input: UpdateTaskInput): Promise<TaskItem> {
    const before = items.value.find((t) => t.id === id)
    const saved = await updateTask(id, input)
    forgetRelated(before ?? {})
    forgetRelated(saved)
    status.value = 'idle'
    return saved
  }

  async function remove(id: string): Promise<void> {
    const before = items.value.find((t) => t.id === id)
    await deleteTask(id)
    items.value = items.value.filter((t) => t.id !== id)
    forgetRelated(before ?? {})
  }

  function invalidate(): void {
    requestSeq++
    items.value = []
    nextCursor.value = null
    status.value = 'idle'
    filter.value = 'open'
  }

  return {
    filter,
    items,
    nextCursor,
    status,
    loadingMore,
    load,
    loadMore,
    setFilter,
    markDone,
    reopen,
    findOne,
    create,
    update,
    remove,
    invalidate,
  }
})
